import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type { DayReflection } from '../types/reflection.js';
import * as reflectionApi from './reflection-api.js';
import { wsClient } from './websocket-client.js';

export type ReflectionLoadingState = 'idle' | 'loading' | 'loaded' | 'error';

let sharedNotes: DayReflection[] = [];
let sharedUnprocessedCount = 0;
let sharedLoadingState: ReflectionLoadingState = 'idle';
let sharedError: string | null = null;

const hosts = new Set<ReactiveControllerHost>();

function notifyHosts(): void {
  for (const host of hosts) {
    host.requestUpdate();
  }
}

async function loadNotes(): Promise<void> {
  if (sharedLoadingState === 'loading') return;
  sharedLoadingState = 'loading';
  sharedError = null;
  notifyHosts();

  try {
    const [notes, countResult] = await Promise.all([
      reflectionApi.getNotes(),
      reflectionApi.getCount(),
    ]);
    sharedNotes = notes;
    sharedUnprocessedCount = countResult.unprocessed;
    sharedLoadingState = 'loaded';
  } catch (e) {
    sharedLoadingState = 'error';
    sharedError = e instanceof Error ? e.message : 'Reflexionen konnten nicht geladen werden';
  }

  notifyHosts();
}

async function loadCount(): Promise<void> {
  try {
    const countResult = await reflectionApi.getCount();
    sharedUnprocessedCount = countResult.unprocessed;
    notifyHosts();
  } catch {
    // Silently ignore count refresh errors
  }
}

function onWsMessage(e: Event): void {
  const detail = (e as CustomEvent).detail as { type: string };
  const type = detail.type;

  if (type === 'reflection:created' || type === 'reflection:deleted') {
    void loadNotes();
  } else if (type === 'reflection:processed') {
    void loadNotes();
  }
}

wsClient.addEventListener('ws-message', onWsMessage);

export class ReflectionStateController implements ReactiveController {
  private host: ReactiveControllerHost;

  constructor(host: ReactiveControllerHost) {
    this.host = host;
    host.addController(this);
  }

  hostConnected(): void {
    hosts.add(this.host);
  }

  hostDisconnected(): void {
    hosts.delete(this.host);
  }

  get notes(): DayReflection[] {
    return sharedNotes;
  }

  get unprocessedCount(): number {
    return sharedUnprocessedCount;
  }

  get loadingState(): ReflectionLoadingState {
    return sharedLoadingState;
  }

  get error(): string | null {
    return sharedError;
  }

  async load(): Promise<void> {
    await loadNotes();
  }

  async refreshCount(): Promise<void> {
    await loadCount();
  }

  async refresh(): Promise<void> {
    await this.load();
  }
}
